import { useContext } from "react"
import { Navigate, Outlet, useParams } from "react-router-dom"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { getLinkToOrgs } from "../../LinkUtils"

/**
 * Guards `/orgs/:orgId/*`: renders the page only if the signed-in user has an
 * org with that id, and sends them to the org list otherwise.
 *
 * The id comes from the URL, so it can be anything — a stale bookmark, an org
 * the user lost access to, or the literal `undefined` from a link that was
 * built before any org was known. Without this guard every page below calls
 * `UserDataContextHelper.findOrg`, which throws, and the error element
 * replaces the whole page.
 */
export default function OrgRouteGuard() {
  const params = useParams<{ orgId: string }>()
  const { userData } = useContext(UserDataContext)

  // The app shell renders its own loading state until `userData` is there, so
  // this should not happen. Render nothing rather than redirecting: a redirect
  // on missing data would fight with the shell.
  if (!userData) {
    return null
  }

  const org = UserDataContextHelper.tryFindOrg(params.orgId, userData)
  if (!org) {
    console.warn(`OrgRouteGuard: no org '${params.orgId}' for this user`)
    return <Navigate to={getLinkToOrgs()} replace />
  }

  return <Outlet />
}
